// Implements: F-IDN-015, F-UIX-009, F-UIX-010
/**
 * The Registro de accesos section: owns `GET /auth/access-log` and the filter, and draws the three
 * states as components (F-UIX-010).
 *
 * "Últimos 30 días" is the server's window (audit 2026-10-19): the server measures it, filters "Solo
 * fallidos y bloqueados" (`failed_only`) before it cuts a page, and names the next page in
 * `next_cursor`, which "Cargar más" asks. Each filter is its own list under its own key; while the
 * other filter's first page loads, the rows already shown stay (no skeleton flash, the focus stays
 * on the filter), dimmed and `aria-busy`: they answer the previous filter, so they are never
 * presented as the active one's result (verifier follow-up 2026-10-19); the query names no next page
 * for placeholder pages, so they offer no "Cargar más". With no rows to keep, the loading state
 * stands in instead of an empty sentence. A further page that fails keeps the rows loaded and
 * offers "Cargar más" again.
 */

import { keepPreviousData, useInfiniteQuery } from "@tanstack/react-query";
import type { InfiniteData } from "@tanstack/react-query";
import { useState } from "react";
import type { ReactElement } from "react";

import type { ApiError } from "../../../shared/api/ApiError";
import { useTranslation } from "../../../shared/i18n";
import { Button } from "../../../shared/ui/atoms/Button";
import { EmptyState } from "../../../shared/ui/molecules/EmptyState";
import { ErrorState } from "../../../shared/ui/molecules/ErrorState";
import { TableSkeleton } from "../../../shared/ui/molecules/TableSkeleton";
import type { AccessLogPage } from "../../../types/api";
import { accessLogPagesKey, fetchAccessLogPage } from "../api/accountApi";
import { AccessLogTable } from "../components/AccessLogTable";
import { AccessLogToolbar } from "../components/AccessLogToolbar";
import type { AccessFilter } from "../components/AccessLogToolbar";
import { GeoAttribution } from "../components/GeoAttribution";
import { accountSectionHref } from "../components/AccountTabs";
import { useMeQuery } from "../hooks/useMe";
import styles from "./AccessLogContainer.module.css";

export function AccessLogContainer(): ReactElement {
  const { t } = useTranslation();
  const me = useMeQuery();
  const [filter, setFilter] = useState<AccessFilter>("all");
  const failedOnly = filter === "bad";
  const log = useInfiniteQuery<
    AccessLogPage,
    ApiError,
    InfiniteData<AccessLogPage, string | undefined>,
    readonly string[],
    string | undefined
  >({
    queryKey: accessLogPagesKey(failedOnly),
    queryFn: ({ pageParam, signal }) => fetchAccessLogPage({ cursor: pageParam, failedOnly }, signal),
    initialPageParam: undefined,
    getNextPageParam: (last) => last.next_cursor ?? undefined,
    placeholderData: keepPreviousData,
  });

  if (log.isPending) return <TableSkeleton />;
  if (log.data === undefined) {
    return (
      <ErrorState
        error={log.error}
        title={t("account.access.loadError")}
        onRetry={() => {
          void log.refetch();
        }}
      />
    );
  }

  const records = log.data.pages.flatMap((page) => page.records);
  // The pages of the previous filter, kept on screen while this filter's first page loads.
  const stale = log.isPlaceholderData;
  return (
    <>
      <AccessLogToolbar
        filter={filter}
        onFilter={setFilter}
        anyBlocked={records.some((record) => record.outcome === "blocked")}
        totpEnabled={me.data?.totp_enabled ?? false}
        passwordHref={accountSectionHref("contrasena")}
        totpHref={accountSectionHref("2fa")}
      />
      {records.length > 0 ? (
        <AccessLogTable records={records} busy={stale} />
      ) : stale ? (
        <TableSkeleton />
      ) : failedOnly ? (
        <EmptyState icon="history" title={t("account.access.emptyFiltered")} body={t("account.access.emptyFilteredBody")} />
      ) : (
        <EmptyState icon="history" title={t("account.access.empty")} body={t("account.access.emptyBody")} />
      )}
      {log.isFetchNextPageError ? (
        <p className={styles.moreFailed} role="alert">
          {t("account.access.moreFailed")}
        </p>
      ) : null}
      {log.hasNextPage ? (
        <div className={styles.more}>
          <Button
            variant="secondary"
            loading={log.isFetchingNextPage}
            onClick={() => {
              void log.fetchNextPage();
            }}
          >
            {t("account.access.more")}
          </Button>
        </div>
      ) : null}
      <GeoAttribution />
    </>
  );
}
